import { useState, useRef, useEffect, useCallback, memo } from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
} from '../ui/dropdown-menu';
import { ChevronDownRegular } from '@fluentui/react-icons';
import { useGlobalTranslate, languageOptions } from '../../hooks';
import { cn } from '../../helpers';
import { capitalize } from 'radash';

// Délai suffisant pour passer du trigger au contenu (portail) sans fermer
const HOVER_CLOSE_DELAY_MS = 350;
const MOBILE_MAX_WIDTH = 768;

type LanguageOption = { code: string; value: string };

const LanguageItem = memo(function LanguageItem({
  language,
  isSelected,
  onSelect,
}: {
  language: LanguageOption;
  isSelected: boolean;
  onSelect: (code: string) => void;
}) {
  const handleCheckedChange = useCallback(() => {
    onSelect(language.code);
  }, [language.code, onSelect]);
  return (
    <DropdownMenuCheckboxItem
      checked={isSelected}
      onCheckedChange={handleCheckedChange}
      className="h-10 cursor-pointer group hover:bg-blue-95 hover:text-blue-00  !px-2"
    >
      {language.value}
    </DropdownMenuCheckboxItem>
  );
});

const LanguageSelectorComponent = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openedAtRef = useRef<number>(0);
  const [isMobile, setIsMobile] = useState(false);
  const { switchLanguage, userLanguage } = useGlobalTranslate();

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
    setIsMobile(mq.matches);
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    if (!isMobile) {
      const recentlyOpened = Date.now() - openedAtRef.current < 200;
      if (recentlyOpened) return;
      clearCloseTimeout();
      closeTimeoutRef.current = setTimeout(() => setIsOpen(false), HOVER_CLOSE_DELAY_MS);
    }
  }, [isMobile, clearCloseTimeout]);

  const handlePointerEnter = useCallback(() => {
    if (!isMobile) {
      clearCloseTimeout();
      if (!isOpen) openedAtRef.current = Date.now();
      setIsOpen(true);
    }
  }, [isMobile, isOpen, clearCloseTimeout]);

  const handlePointerLeave = useCallback(() => {
    if (!isMobile) scheduleClose();
  }, [isMobile, scheduleClose]);

  const handleLanguageChange = useCallback(
    (code: string) => {
      switchLanguage(code);
    },
    [switchLanguage],
  );

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <div
          className={cn(
            'flex items-center gap-2 px-2 py-1 h-min hover:bg-blue-95 rounded-xl cursor-pointer',
          )}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
        >
          {capitalize(userLanguage)}
          <ChevronDownRegular
            fontSize={16}
            className={`transition-transform duration-100 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        className="!px-0 py-0"
      >
        {languageOptions.map((language) => (
          <LanguageItem
            key={language.code}
            language={language}
            isSelected={userLanguage === language.code}
            onSelect={handleLanguageChange}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const LanguageSelector = memo(LanguageSelectorComponent);
export default LanguageSelector;
